import { useState } from 'react';
import { useTranslation } from '@eon/i18n';
import {
  Badge,
  Button,
  DataTable,
  Input,
  PageHeader,
  Pagination,
  SelectField,
  type Column,
} from '@eon/ui';
import { hooks, type SystemLog } from '@eon/api-client';
import { formatDate } from '@eon/utils';
import { usePaginationLabels } from '@/lib/use-pagination-labels';
import { useTableSort } from '@/lib/table-sort';
import { ChevronDown, ChevronRight, RefreshCw, Search } from 'lucide-react';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const ALL_LEVELS = '__all__';
const ALL_SOURCES = '__all__';

const LEVEL_VARIANT: Record<string, 'success' | 'info' | 'warning' | 'destructive' | 'secondary'> =
  {
    debug: 'secondary',
    info: 'info',
    warn: 'warning',
    error: 'destructive',
  };

function levelKey(level: string): string {
  const l = level.toLowerCase();
  if (l === 'warning') return 'warn';
  return l;
}

function formatFields(fields: Record<string, unknown> | null | undefined): string {
  if (!fields || Object.keys(fields).length === 0) return '';
  try {
    return JSON.stringify(fields, null, 2);
  } catch {
    return String(fields);
  }
}

export function LogsPage() {
  const { t } = useTranslation();
  const labels = usePaginationLabels();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [q, setQ] = useState('');
  const [level, setLevel] = useState(ALL_LEVELS);
  const [source, setSource] = useState(ALL_SOURCES);
  const [expanded, setExpanded] = useState<string | null>(null);

  const { sort, onSortChange, sortParams } = useTableSort(() => setPage(1));
  const list = hooks.useSystemLogs({
    page,
    pageSize,
    q: q.trim() || undefined,
    level: level === ALL_LEVELS ? undefined : level,
    source: source === ALL_SOURCES ? undefined : source,
    ...sortParams,
  });

  const levelOptions = [
    { value: ALL_LEVELS, label: t('filters.all') },
    { value: 'debug', label: t('logs.level.debug') },
    { value: 'info', label: t('logs.level.info') },
    { value: 'warn', label: t('logs.level.warn') },
    { value: 'error', label: t('logs.level.error') },
  ];

  const sourceOptions = [
    { value: ALL_SOURCES, label: t('filters.all') },
    { value: 'ocpp', label: t('logs.source.ocpp') },
    { value: 'api', label: t('logs.source.api') },
    { value: 'worker', label: t('logs.source.worker') },
    { value: 'device', label: t('logs.source.device') },
  ];

  const columns: Column<SystemLog>[] = [
    {
      key: 'expand',
      header: '',
      className: 'w-8',
      cell: (row) => {
        const hasFields = row.fields && Object.keys(row.fields).length > 0;
        if (!hasFields) return null;
        const open = expanded === row.id || expanded === `${row.time}-${row.message}`;
        const key = row.id || `${row.time}-${row.message}`;
        return (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setExpanded(open ? null : key)}
          >
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        );
      },
    },
    {
      key: 'time',
      header: t('logs.col.time'),
      sortable: true,
      className: 'whitespace-nowrap',
      sortValue: (row) => row.time,
      cell: (row) => (
        <span className="text-muted-foreground font-mono text-xs">
          {formatDate(row.time, 'yyyy-MM-dd HH:mm:ss')}
        </span>
      ),
    },
    {
      key: 'level',
      header: t('logs.col.level'),
      sortable: true,
      className: 'w-24',
      cell: (row) => (
        <Badge variant={LEVEL_VARIANT[levelKey(row.level)] ?? 'secondary'}>
          {t(`logs.level.${levelKey(row.level)}`)}
        </Badge>
      ),
    },
    {
      key: 'source',
      header: t('logs.col.source'),
      sortable: true,
      className: 'w-28',
      cell: (row) => (
        <Badge variant="outline" className="font-mono text-[10px] uppercase">
          {row.source ?? '—'}
        </Badge>
      ),
    },
    {
      key: 'message',
      header: t('logs.col.message'),
      sortable: true,
      sortValue: (row) => row.message,
      cell: (row) => (
        <div className="space-y-1">
          <p className="text-sm leading-snug break-words">{row.message}</p>
          {expanded === (row.id || `${row.time}-${row.message}`) && row.fields ? (
            <pre className="bg-muted text-muted-foreground max-h-40 overflow-auto rounded-md p-2 text-[11px]">
              {formatFields(row.fields)}
            </pre>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('nav.logs')}
        description={t('logs.description')}
        actions={
          <Button variant="outline" size="sm" onClick={() => list.refetch()} disabled={list.isFetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${list.isFetching ? 'animate-spin' : ''}`} />
            {t('logs.refresh')}
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1 space-y-1.5">
          <label className="text-muted-foreground text-xs font-medium">{t('actions.search')}</label>
          <div className="relative">
            <Search className="text-muted-foreground absolute top-2.5 left-2.5 h-4 w-4" />
            <Input
              className="pl-9"
              placeholder={t('logs.searchPlaceholder')}
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>
        <div className="w-full space-y-1.5 sm:w-40">
          <label className="text-muted-foreground text-xs font-medium">{t('logs.col.level')}</label>
          <SelectField
            value={level}
            options={levelOptions}
            onValueChange={(v) => {
              setLevel(v);
              setPage(1);
            }}
          />
        </div>
        <div className="w-full space-y-1.5 sm:w-44">
          <label className="text-muted-foreground text-xs font-medium">{t('logs.col.source')}</label>
          <SelectField
            value={source}
            options={sourceOptions}
            onValueChange={(v) => {
              setSource(v);
              setPage(1);
            }}
          />
        </div>
      </div>

      <p className="text-muted-foreground mb-3 text-xs">{t('logs.hint')}</p>

      <DataTable
        data={list.data?.data}
        columns={columns}
        loading={list.isLoading}
        rowKey={(row) => row.id ?? `${row.time}-${row.message}`}
        emptyTitle={t('logs.empty')}
        emptyDescription={t('logs.emptyHint')}
        sort={sort}
        onSortChange={onSortChange}
      />

      {list.data && (list.data.totalCount ?? 0) > 0 ? (
        <Pagination
          page={page}
          totalPages={list.data.totalPages ?? 1}
          totalCount={list.data.totalCount ?? 0}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          loading={list.isFetching}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          labels={labels}
        />
      ) : null}
    </>
  );
}
